import { z } from 'zod';

// ---------------------------------------------------------------------------
// GET /v1/interiors?wallpaper_id=&room_category_id=&status=&limit=&offset=
//
// Service-role read facade over `wallpapers_interiors` (RLS blocks anon).
// Returns ready, non-hidden AI-generated interior shots for a wallpaper.
// The raw `prompt_snapshot` JSONB is NEVER returned — only the lifted display
// fields (mood/lighting/style_era/chaos) are exposed.
//
// IDs are BIGINT in the DB (wallpapers_interiors.id / wallpaper_id /
// room_category_id) → modelled as positive ints here (NOT UUID like prices).
//
// Each InteriorItem carries alt + caption (composed alt text).
// Results ordered by sort_order ASC NULLS LAST, id ASC.
// ---------------------------------------------------------------------------

export const InteriorsRequestSchema = z.object({
  wallpaper_id: z.coerce.number().int().positive(),
  /** Numeric room category ID. Omit to return all rooms. */
  room_category_id: z.coerce.number().int().positive().optional(),
  status: z.string().min(1).default('ready'),
  limit: z.coerce.number().int().min(1).max(100).default(24),
  offset: z.coerce.number().int().min(0).default(0),
  /** BCP-47 locale for alt/caption composition. Defaults to "pl" (primary storefront). */
  lang: z.string().min(2).max(10).default('pl'),
});
export type InteriorsRequest = z.infer<typeof InteriorsRequestSchema>;

export const InteriorItemSchema = z.object({
  id: z.number().int().positive(),
  wallpaper_id: z.number().int().positive(),
  room_category_id: z.number().int().positive(),
  image_url: z.string().url(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  mood: z.string().nullable(),
  lighting: z.string().nullable(),
  style_era: z.string().nullable(),
  chaos: z.number().nullable(),
  /** Composed alt text: "fototapeta <wallpaper-name> — <room-ending>", max 125 chars. */
  alt: z.string(),
  /** Sitemap image caption — identical to alt in Phase-2; shipped separately for future divergence. */
  caption: z.string(),
});
export type InteriorItem = z.infer<typeof InteriorItemSchema>;

export const InteriorsResponseSchema = z.object({
  items: z.array(InteriorItemSchema),
  total: z.number().int().nonnegative(),
});
export type InteriorsResponse = z.infer<typeof InteriorsResponseSchema>;
